import React, {
  useCallback, useState, useEffect, useMemo,
} from 'react';
import * as Styles from './ChangeTitanDomainHandler.styles';
import HgLogoV2 from '@/media/logo/HgLogoV2';
import { Redirect, useHistory, useParams } from 'react-router';
import { AVAILABILITY_STATUS, STEPS } from './ChangeTitanDomainHandler.enum';
import { InputDomain } from './components/InputDomain';
import { domain as isDomain } from '@/utils/Validators/validation';
import useLocale from '@/hooks/useLocale';
import { AlertV2, IconDanger, Skeleton } from 'gatorcomponents';
import { useResize } from '@/hooks/useResize';
import { NavigatorDetail } from '@/media/changeDomain';
import { useDispatch, useSelector } from 'react-redux';
import { BreadCrumbs } from '../ChangeDomainHandler/components/BreadCrumbs';
import { emailsActions } from '@/redux/modules/emails';
import { DomainNotRegistered } from './components/DomainNotRegistered';
import { FlowFinished } from './components/FlowFinished';
import SnappyIdea from '@/media/domains/snappy-idea.svg';
import { CART_URL } from '@/config';
import { ChangeDomain } from './components/ChangeDomain';
import SnappyCongrats from '@/media/domains/snappy-success.svg';
import LoaderPage from '@/pages/Loader/LoaderPage';

const ChangeTitanDomainHandler = () => {
  const history = useHistory();
  const { changeDomain: changeDomainLocale, titanWithoutDomain: titanWithoutDomainLocale, routes: routesLocale } = useLocale();
  const windowRect = useResize();
  const params = useParams();
  const dispatch = useDispatch();
  const emailsReducer = useSelector(state => state.emails);
  const currentProEmail = useMemo(() => emailsReducer.proEmailList.find(email => email.hosting_id && email.hosting_id.toString() === params.hostingId), [emailsReducer.proEmailList, params.hostingId]);

  const { width } = windowRect;
  const mobile = width < 960;
  const isLoading = emailsReducer.loading || emailsReducer.loadingCheckDomainAvailability;
  const initialStep = STEPS.INPUT_DOMAIN;

  const [currentStep, setCurrentStep] = useState(initialStep);
  const [currentProEmailDomain, setCurrentProEmailDomain] = useState('');
  const [domainIsOk, setDomainIsOk] = useState(false);
  const [newDomain, setNewDomain] = useState({
    value: '',
    error: '',
  });
  const [error, setError] = useState({
    error: false,
    message: '',
    title: '',
  });

  const handleConfirmChangeDomain = useCallback(() => {
    dispatch(emailsActions.checkDomainAvailabilityToTitan.request({
      domain: newDomain.value,
      onErrorCallBack: () => setError({
        error: true,
        message: titanWithoutDomainLocale.changeDomainPage.inputPage.error.message,
        title: titanWithoutDomainLocale.changeDomainPage.inputPage.error.title,
      }),
    }));
    setError({
      error: false,
      message: '',
      title: '',
    });
  }, [dispatch, newDomain.value, titanWithoutDomainLocale]);

  const handleResetFlow = useCallback(() => {
    setCurrentStep(STEPS.INPUT_DOMAIN);
    setNewDomain({ value: '', error: '' });
    setDomainIsOk(false);
    dispatch(emailsActions.checkDomainAvailabilityToTitan.reset());
  }, [dispatch]);

  const goBackToEmailsList = useCallback(() => {
    history.push(routesLocale.emailsList);
    handleResetFlow();
  }, [history, routesLocale.emailsList, handleResetFlow]);

  const initialBreadCrumbs = useMemo(() => [
    {
      label: titanWithoutDomainLocale.changeDomainPage.breadcrumbs.email,
      onClick: () => goBackToEmailsList(),
    },
    {
      label: titanWithoutDomainLocale.changeDomainPage.breadcrumbs.changeDomain,
    },
  ], [titanWithoutDomainLocale, goBackToEmailsList]);
  const availabilityStatus = useMemo(() => emailsReducer.changeTitanDomain.status, [emailsReducer.changeTitanDomain.status]);

  const [breadCrumbs, setBreadCrumbs] = useState(initialBreadCrumbs);

  const getInputDomainProps = useCallback(() => {
    const defaultProps = {
      title: titanWithoutDomainLocale.changeDomainPage.inputPage.title,
      description: titanWithoutDomainLocale.changeDomainPage.inputPage.description,
      continueCta: titanWithoutDomainLocale.changeDomainPage.inputPage.buttonContinueLabel,
      backCta: titanWithoutDomainLocale.changeDomainPage.inputPage.buttonBackLabel,
      alertDescription: titanWithoutDomainLocale.changeDomainPage.inputPage.alertDescription,
      newDomain,
      domainIsOk,
      loadingChange: false,
      originalDomain: currentProEmailDomain,
      mySiteLabel: mobile ? titanWithoutDomainLocale.changeDomainPage.inputPage.inputPlaceholderMobile : titanWithoutDomainLocale.changeDomainPage.inputPage.inputPlaceholder,
    };

    const validateDomain = (domain) => {
      const domainIsValid = isDomain(domain);

      if (domainIsValid) {
        setDomainIsOk(true);
        setNewDomain({
          value: domain,
          error: domain === currentProEmailDomain ? changeDomainLocale.steps.inputDomain.domainEqualOriginalDomain : '',
        });
      }

      if (!domainIsValid) {
        setDomainIsOk(false);
        if (newDomain.value.includes('.br')) {
          setNewDomain({
            value: domain,
            error: changeDomainLocale.steps.inputDomain.domainMessageErrorBr,
          });
          return;
        }

        if (!newDomain.value.includes('.br')) {
          setNewDomain({
            value: domain,
            error: changeDomainLocale.steps.inputDomain.domainMessageError,
          });
        }
      }
    };

    const onContinue = () => {
      handleConfirmChangeDomain();
    };

    const onChangeDomain = (e) => {
      validateDomain(e.target.value.toLowerCase());
    };

    const onBack = () => {
      history.goBack();
    };

    return {
      ...defaultProps,
      onChangeDomain,
      onBack,
      onContinue,
    };
  }, [
    domainIsOk,
    newDomain,
    changeDomainLocale,
    history,
    handleConfirmChangeDomain,
    titanWithoutDomainLocale,
    currentProEmailDomain,
    mobile,
  ]);

  const getDomainNotRegisteredProps = useCallback(() => {
    const goToPurchase = () => {
      const domain = newDomain.value.replace(/^www\./, '');
      const domainSld = domain.split('.')[0];
      const domainTld = `.${domain.split('.').slice('1').join('.')}`;

      window.open(`${CART_URL}?pid=d&sld=${domainSld}&tld=${domainTld}&domainCycle=1`, '_blank');

      setCurrentStep(STEPS.NOT_REGISTERED_SUCCESS);
    };

    const defaultProps = {
      locale: titanWithoutDomainLocale.changeDomainPage.notRegistered,
      backButton: () => handleResetFlow(),
      goToPurchase: () => {
        goToPurchase();
        setCurrentStep(STEPS.FLOW_FINISHED_BUYING_DOMAIN);
      },
      newDomain: newDomain.value,
      mySiteLabel: newDomain.value,
      mobile,
    };
    return defaultProps;
  }, [newDomain.value, titanWithoutDomainLocale, handleResetFlow, mobile]);

  const getFlowFinishedInBuyingDomainProps = useCallback(() => {
    const defaultProps = {
      SnappyImage: SnappyIdea,
      button: {
        label: titanWithoutDomainLocale.changeDomainPage.buyingDomain.buttonLabel,
        action: () => goBackToEmailsList(),
        variant: 'tertiary',
        iconRight: true,
      },
      locale: titanWithoutDomainLocale.changeDomainPage.buyingDomain,
      mobile,
    };
    return defaultProps;
  }, [titanWithoutDomainLocale, goBackToEmailsList, mobile]);

  const getFlowFinishedByTitanNotAvailableProps = useCallback(() => {
    const defaultProps = {
      button: {
        label: titanWithoutDomainLocale.changeDomainPage.notAvailableInTitan.buttonLabel,
        action: () => handleResetFlow(),
        variant: 'tertiary',
        iconRight: false,
      },
      locale: titanWithoutDomainLocale.changeDomainPage.notAvailableInTitan,
      icon: <IconDanger color="primaryBlue" />,
      newDomain: newDomain.value,
      mobile,
    };
    return defaultProps;
  }, [titanWithoutDomainLocale, handleResetFlow, newDomain, mobile]);

  const getChangeDomainInternalProps = useCallback(() => {
    const handleChangeDomainInternal = () => {
      dispatch(emailsActions.changeTitanDomain.request({
        domain: newDomain.value,
        hostingId: params.hostingId,
        isInternal: true,
        platformId: emailsReducer.changeTitanDomain.platformId,
        callBackFunction: () => setCurrentStep(STEPS.CHANGED_INTERNAL_TITAN_DOMAIN),
        onFailureCallBackFunction: () => setCurrentStep(STEPS.CHANGE_DOMAIN_FAILED),
      }));
    };

    const defaultProps = {
      backButton: {
        label: titanWithoutDomainLocale.changeDomainPage.internalDomain.backButtonLabel,
        action: () => handleResetFlow(),
        variant: 'tertiary',
        iconRight: false,
      },
      continueButton: {
        label: titanWithoutDomainLocale.changeDomainPage.internalDomain.continueButtonLabel,
        action: () => handleChangeDomainInternal(),
        variant: 'primary',
        iconRight: false,
      },
      locale: titanWithoutDomainLocale.changeDomainPage.internalDomain,
      newDomain: newDomain.value,
      mobile,
    };
    return defaultProps;
  }, [titanWithoutDomainLocale, handleResetFlow, newDomain, mobile, dispatch, params.hostingId, emailsReducer.changeTitanDomain.platformId]);

  const getChangeDomainExternalProps = useCallback(() => {
    const handleChangeDomainExternal = () => {
      dispatch(emailsActions.changeTitanDomain.request({
        domain: newDomain.value,
        hostingId: params.hostingId,
        isInternal: false,
        platformId: emailsReducer.changeTitanDomain.platformId,
        callBackFunction: () => {
          window.location.href = `${routesLocale.emailsList}${routesLocale.emailsTitan}/${newDomain.value}`;
        },
        onFailureCallBackFunction: () => setCurrentStep(STEPS.CHANGE_DOMAIN_FAILED),
      }));
    };

    const defaultProps = {
      backButton: {
        label: titanWithoutDomainLocale.changeDomainPage.externalDomain.backButtonLabel,
        action: () => handleResetFlow(),
        variant: 'tertiary',
        iconRight: false,
      },
      continueButton: {
        label: titanWithoutDomainLocale.changeDomainPage.externalDomain.continueButtonLabel,
        action: () => handleChangeDomainExternal(),
        variant: 'primary',
        iconRight: true,
      },
      locale: titanWithoutDomainLocale.changeDomainPage.externalDomain,
      newDomain: newDomain.value,
      icon: <IconDanger color="primaryBlue" />,
      mobile,
    };
    return defaultProps;
  }, [titanWithoutDomainLocale, handleResetFlow, newDomain, mobile, dispatch, emailsReducer.changeTitanDomain.platformId, params.hostingId, routesLocale]);

  const getChangedInternalTitanDomainProps = useCallback(() => {
    const defaultProps = {
      SnappyImage: SnappyCongrats,
      button: {
        label: titanWithoutDomainLocale.changeDomainPage.changedInternalTitanDomain.buttonLabel,
        action: () => {
          window.location.href = `${routesLocale.emailsList}${routesLocale.emailsTitan}/${newDomain.value}`;
        },
        variant: 'primary',
        iconRight: false,
      },
      locale: titanWithoutDomainLocale.changeDomainPage.changedInternalTitanDomain,
      mobile,
    };
    return defaultProps;
  }, [titanWithoutDomainLocale, newDomain.value, routesLocale, mobile]);

  const getFlowFinishedFailureProps = useCallback(() => {
    const defaultProps = {
      button: {
        label: titanWithoutDomainLocale.changeDomainPage.changeDomainFail.buttonLabel,
        action: () => goBackToEmailsList(),
        variant: 'primary',
        iconRight: false,
      },
      locale: titanWithoutDomainLocale.changeDomainPage.changeDomainFail,
      icon: <IconDanger color="primaryBlue" />,
      mobile,
    };
    return defaultProps;
  }, [titanWithoutDomainLocale, goBackToEmailsList, mobile]);

  const renderStep = {
    [STEPS.INPUT_DOMAIN]: <InputDomain {...getInputDomainProps()} />,
    [STEPS.DOMAIN_NOT_REGISTERED]: <DomainNotRegistered {...getDomainNotRegisteredProps()} />,
    [STEPS.FLOW_FINISHED_BUYING_DOMAIN]: <FlowFinished {...getFlowFinishedInBuyingDomainProps()} />,
    [STEPS.DOMAIN_NOT_AVAILABLE_IN_TITAN]: <FlowFinished {...getFlowFinishedByTitanNotAvailableProps()} />,
    [STEPS.DOMAIN_EXIST_INTERNAL]: <ChangeDomain {...getChangeDomainInternalProps()} />,
    [STEPS.DOMAIN_EXIST_EXTERNAL]: <ChangeDomain {...getChangeDomainExternalProps()} />,
    [STEPS.CHANGED_INTERNAL_TITAN_DOMAIN]: <FlowFinished {...getChangedInternalTitanDomainProps()} />,
    [STEPS.CHANGE_DOMAIN_FAILED]: <FlowFinished {...getFlowFinishedFailureProps()} />,
  };

  const requestData = useCallback(() => {
    if (!emailsReducer.proEmailList.length) {
      dispatch(emailsActions.emails.request());
    }
  }, [dispatch, emailsReducer.proEmailList]);

  useEffect(() => {
    requestData();
  }, [requestData]);

  useEffect(() => {
    setCurrentProEmailDomain(currentProEmail ? currentProEmail.domain : '');
  }, [currentProEmail]);

  useEffect(() => {
    if (availabilityStatus) {
      switch (availabilityStatus) {
        case AVAILABILITY_STATUS.DOMAIN_OK:
          setCurrentStep(STEPS.DOMAIN_EXIST_INTERNAL);
          break;
        case AVAILABILITY_STATUS.TITAN_NOT_AVAILABLE:
          setCurrentStep(STEPS.DOMAIN_NOT_AVAILABLE_IN_TITAN);
          break;
        case AVAILABILITY_STATUS.DOMAIN_ANOTHER_PROVIDER:
          setCurrentStep(STEPS.DOMAIN_EXIST_EXTERNAL);
          break;
        case AVAILABILITY_STATUS.DOMAIN_NOT_AVAILABLE:
          setCurrentStep(STEPS.DOMAIN_NOT_REGISTERED);
          break;
        default:
          handleResetFlow();
      }
    }
  }, [availabilityStatus, handleResetFlow]);

  useEffect(() => {
    setBreadCrumbs([
      {
        label: titanWithoutDomainLocale.changeDomainPage.breadcrumbs.email,
        onClick: () => goBackToEmailsList(),
      },
      {
        label: currentStep === STEPS.DOMAIN_NOT_REGISTERED ? titanWithoutDomainLocale.changeDomainPage.breadcrumbs.buyDomain : titanWithoutDomainLocale.changeDomainPage.breadcrumbs.changeDomain,
      },
    ]);
  }, [currentStep, titanWithoutDomainLocale, history, routesLocale, goBackToEmailsList]);

  const hostingIdExists = emailsReducer.proEmailList.some(email => email.hosting_id && email.hosting_id.toString() === params.hostingId);

  const redirectUserOut = !hostingIdExists && emailsReducer.proEmailList.length > 0;

  if (isLoading) {
    return (
      <Styles.ChangeDomainWrapper>
        <Styles.LogoRow>
          <HgLogoV2 newMenu />
        </Styles.LogoRow>
        <Styles.SkeletonWrapper>
          <Styles.TopContent>
            <Skeleton variant="rounded" width="306px" height="24px" />
            <Skeleton variant="rounded" width="368px" height="32px" />
            <Skeleton variant="rounded" width="319px" height="24px" />
          </Styles.TopContent>
          {!mobile && (
            <NavigatorDetail />
          )}
          <Skeleton width="calc(100% - 40px)" height="56px" />
          <Styles.BottomContent>
            <Skeleton width="100%" height="108px" />
            <Skeleton variant="rounded" width={mobile ? '100%' : '126px'} height="48px" />
          </Styles.BottomContent>
        </Styles.SkeletonWrapper>
      </Styles.ChangeDomainWrapper>
    );
  }

  if (redirectUserOut) {
    return (
      <Redirect to={routesLocale.emailsList} />
    );
  }

  return (
    <>
      {emailsReducer.changeTitanDomain.loading && (
        <Styles.LoadWrapper>
          <Styles.LoadingLogoRow>
            <HgLogoV2 newMenu />
          </Styles.LoadingLogoRow>
          <Styles.LoaderWrapper>
            <LoaderPage progress={50} title={titanWithoutDomainLocale.changeDomainPage.loadingLabel} />
          </Styles.LoaderWrapper>
        </Styles.LoadWrapper>
      )}
      <Styles.ChangeDomainWrapper>
        <Styles.LogoRow>
          <HgLogoV2 newMenu />
        </Styles.LogoRow>

        <Styles.Container>
          <Styles.BreadCrumbsWrapper>
            <BreadCrumbs breadCrumbs={breadCrumbs} />
          </Styles.BreadCrumbsWrapper>
          {renderStep[currentStep]}
        </Styles.Container>
      </Styles.ChangeDomainWrapper>
      {error.error && (
      <Styles.AlertWrapper>
        <AlertV2
          description={error.message}
          key="errors"
          onClose={() => setError({ error: false, message: '', title: '' })}
          title={error.title}
          variant="critical"
          width="full"
          withoutBackground={false}
        />
      </Styles.AlertWrapper>
      )}
    </>
  );
};

export default ChangeTitanDomainHandler;
